// The words for what a schema refuses — ONE map for every form (and every other parse in the browser). Zod's own
// checks speak the reader's language ("Required" · "At most 64 characters" · "At least 1" · "Not an email address"),
// and a schema's own message is a CODE whose sentence lives in the `errors` words (`PHONE_E164`) — never English in a
// schema, never a word per field, never a raw code on screen. The map is installed once at boot (`i18n/index.ts`);
// every form resolves through `formResolver` (lint-enforced — the bare Zod resolver is banned elsewhere), and the
// import preview reads its row messages through `validationMessage` too.

import { zodResolver } from '@hookform/resolvers/zod';
import i18n from 'i18next';
import type { FieldValues, Resolver } from 'react-hook-form';
import { z } from 'zod';
import { formatNumber } from './format.js';

const CODE = /^[A-Z][A-Z0-9_]*$/;
const words = (key: string, opts?: Record<string, unknown>): string => i18n.t(`common:validation.${key}`, opts);

/** A schema's message as the reader reads it: a CODE → its sentence (the `errors` words — "Not valid" when it has
 *  none, so a raw code never reaches a person); anything else as it came (already words). */
export function validationMessage(message: string): string {
  if (!CODE.test(message)) return message;
  return i18n.t(`errors:${message}`, { defaultValue: words('invalid') });
}

/** Zod's own checks in the reader's words — every issue a schema did not word itself. */
const issueMessage: z.core.$ZodErrorMap = (iss) => {
  switch (iss.code) {
    case 'invalid_type':
      if (iss.input === undefined || iss.input === null || iss.input === '') return words('required');
      return String(iss.expected) === 'int' ? words('wholeNumber') : String(iss.expected) === 'number' ? words('number') : words('invalid');
    case 'too_small': {
      const n = Number(iss.minimum);
      if (iss.origin === 'string') return iss.exact ? words('exactLength', { count: n }) : n <= 1 ? words('required') : words('tooShort', { count: n });
      if (iss.origin === 'array' || iss.origin === 'set') return n <= 1 ? words('chooseOne') : words('chooseAtLeast', { count: n });
      if (iss.origin === 'number' || iss.origin === 'bigint') return words(iss.inclusive === false ? 'above' : 'atLeast', { value: formatNumber(n) });
      return words('invalid');
    }
    case 'too_big': {
      const n = Number(iss.maximum);
      if (iss.origin === 'string') return iss.exact ? words('exactLength', { count: n }) : words('tooLong', { count: n });
      if (iss.origin === 'array' || iss.origin === 'set') return words('chooseAtMost', { count: n });
      if (iss.origin === 'number' || iss.origin === 'bigint') return words(iss.inclusive === false ? 'below' : 'atMost', { value: formatNumber(n) });
      return words('invalid');
    }
    case 'invalid_format':
      return iss.format === 'email' ? words('email') : iss.format === 'url' ? words('url') : words('format');
    case 'invalid_value':
      return words('choice');
    default:
      return words('invalid');
  }
};

/** Installs the one map — once, at boot. */
export function installValidationMessages(): void {
  z.config({ customError: issueMessage });
}

type AnyResolver = (...args: Parameters<Resolver<FieldValues>>) => ReturnType<Resolver<FieldValues>>;

/** Every message of a resolver's answer read as the reader reads it — through every nesting (a SIM row's number, a
 *  service block's field); never into a field's `ref` (its DOM element). */
function localize(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  for (const [key, child] of Object.entries(node)) {
    if (key === 'ref') continue;
    if (key === 'message' && typeof child === 'string') (node as Record<string, unknown>)[key] = validationMessage(child);
    else localize(child);
  }
}

/** The one resolver: Zod's, its messages in the reader's words (codes read from the `errors` words). Same signature
 *  as the Zod resolver, so a form writes `resolver: formResolver(Schema)`. */
export const formResolver = ((...args: unknown[]): AnyResolver => {
  const resolve = (zodResolver as unknown as (...a: unknown[]) => AnyResolver)(...args);
  return async (values, context, options) => {
    const result = await resolve(values, context, options);
    localize(result.errors);
    return result;
  };
}) as unknown as typeof zodResolver;
